import { tierOf } from '@/shared/permissions/identity';
import { effectiveWorkspaceAccess } from '@/shared/permissions/workspaceAccess';
import type { CurrentUser } from '@/shared/types/auth';

/** What level of the company a dashboard shows. Mirrors src/authority.js#scopeOf. */
export type ViewLevel = 'own' | 'department' | 'company';

export interface DashboardUser {
  role?: string | null;
  department?: string | null;
  permissions?: readonly string[] | null;
  actionPermissions?: readonly string[] | null;
}

/**
 * Derived from the authority ladder alone. `department === 'management'` is the
 * ONLY thing that makes an administrator company-wide — a missing department
 * narrows to 'department' (fail closed), which is the opposite of what
 * Dashboard.tsx did before this file existed (`role === 'admin' && !dept` sent
 * an administrator to the executive dashboard because nothing was set).
 */
export function viewLevelOf(user: DashboardUser | null | undefined): ViewLevel {
  const tier = tierOf(user?.role);
  if (tier === 'SUPER_ADMIN') return 'company';
  if (tier === 'ADMIN') return user?.department === 'management' ? 'company' : 'department';
  return 'own';
}

/**
 * Generalises the deleted `SurajMishraDualDashboard`: an account that leads a
 * team AND still works a personal pipeline gets one Own | Team switch instead
 * of a hardcoded username list. The personal pipeline is exactly the `sales`
 * workspace — the only module whose screens carry own-pipeline numbers — so
 * this is a statement about access, not about identity.
 */
export function isDualView(user: DashboardUser | null | undefined, level: ViewLevel): boolean {
  if (level === 'own') return false;
  return effectiveWorkspaceAccess(user as CurrentUser | null).includes('sales');
}
